import { useState } from 'react';

const faqs = [
  {
    q: 'How long does it take to create an ad with AI using AdForge?',
    a: 'Most users create their first complete, launch-ready ad in under 3 minutes. Setting up your business profile takes about 2 minutes the first time — after that, each generation takes under 30 seconds. The AI generates ad copy and a matching image simultaneously, so there\'s no waiting.',
  },
  {
    q: 'Do I need any marketing experience to use AdForge?',
    a: 'Absolutely not. AdForge is specifically built for people with zero marketing experience. The input form uses plain English — you just describe your business and what you\'re offering. The AI handles all the copywriting strategy, platform formatting, and image generation automatically. If you can describe your business in a few sentences, you can create professional ads.',
  },
  {
    q: 'How does the AI know what kind of ad to write for my business?',
    a: 'AdForge\'s AI ad generator is trained on millions of high-performing ads across industries and platforms. When you provide your business details — industry, target audience, key offer, and tone — the AI selects the most effective ad format, psychological angle, and copywriting framework for your specific situation. It considers your platform choice, campaign goal, and competitive context to generate ads that actually convert.',
  },
  {
    q: 'Can I use AdForge if I\'ve never run ads before?',
    a: 'Yes — and many of our best-loved users are first-time advertisers. AdForge walks you through each step, explains what\'s needed and why, and generates ready-to-use ads you can paste directly into any ad platform. If you\'re new to advertising, our How It Works guide and in-app tooltips explain everything from setting up a Facebook campaign to understanding what makes a good headline.',
  },
  {
    q: 'What happens after I generate an ad — how do I launch it?',
    a: 'Once your AI-generated ad is ready, you have two options: (1) Copy the ad text to your clipboard and download the image, then paste them into your ad manager (Facebook Ads Manager, Google Ads, etc.) directly. (2) Open the ad in AdForge\'s visual editor to make any final tweaks before exporting. The entire process — generation to launch — takes under 5 minutes for most users.',
  },
  {
    q: 'How does AdForge handle different ad formats for each platform?',
    a: 'Every ad generated by AdForge is automatically formatted for the selected platform. This means correct image dimensions (1:1 for Instagram, 16:9 for Facebook, 9:16 for TikTok stories), correct character limits for headlines and descriptions, platform-appropriate tone and style, and relevant ad type structure. You don\'t need to know any of this — AdForge handles it all behind the scenes.',
  },
  {
    q: 'Can I edit the AI-generated ad before launching?',
    a: 'Yes. Every generated ad can be opened in AdForge\'s built-in visual editor where you can change text, swap images, adjust colors, reposition elements, and fine-tune anything before exporting. You can also regenerate individual components — just the image, just one headline variation — without losing the rest of the ad. Most users spend 2–5 minutes editing before they\'re happy with the result.',
  },
];

export default function HIWFAQ() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section id="faq" className="py-20 lg:py-28 bg-stone-50">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3 block">FAQ</span>
          <h2 className="text-3xl lg:text-4xl font-black text-stone-900 mb-4 leading-tight">
            Common Questions About How AdForge Works
          </h2>
          <p className="text-stone-500 text-base">
            Everything beginners ask before creating their first ad with AI — answered.
          </p>
        </div>

        <div
          itemScope
          itemType="https://schema.org/FAQPage"
          className="space-y-3"
        >
          {faqs.map((faq, idx) => (
            <article
              key={idx}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              className="rounded-xl border border-stone-100 bg-white overflow-hidden"
            >
              <button
                className="w-full flex items-start justify-between gap-4 px-6 py-5 text-left cursor-pointer hover:bg-stone-50 transition-colors duration-200"
                onClick={() => setOpen(open === idx ? null : idx)}
                aria-expanded={open === idx}
              >
                <h3 itemProp="name" className="text-sm font-bold text-stone-900 leading-snug">
                  {faq.q}
                </h3>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className={`ri-${open === idx ? 'subtract' : 'add'}-line text-stone-400 text-base`} />
                </div>
              </button>
              {open === idx && (
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer" className="px-6 pb-5">
                  <p itemProp="text" className="text-sm text-stone-500 leading-relaxed">{faq.a}</p>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
